'use client';

import { InfoTooltip } from '@wwe-portal/ui/components/tooltip/info-tooltip';
import { FC, useState } from 'react';

import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@wwe-portal/ui/components/collapsible';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import { cn } from '@wwe-portal/ui/lib/lib';
import { UpgradesAndWinsConsumer } from './upgrades-and-wins-consumer';
import { SelectStarColorConsumer } from './select-star-color-consumer';
import { SelectStarConsumer } from './select-star-consumer';
import { SuperstarLevelConsumer } from './superstar-level-consumer';
import { TotalStatsConsumer } from './total-stats-consumer';
import { AbilitiesConsumer } from './abilities-consumer';
import { RosterCompareButtonConsumer } from './compare-button-consumer';
import { sendSuperstarDetailsSectionExpandEvent } from '@wwe-portal/web/libs/analytics/superstars-v2';
import { useSuperstarDetailId } from './context';
import { CompareVFXConsumer } from './compare-vfx';
export const SuperstarStatsDetails = () => {
  return (
    <div className="relative flex flex-col gap-4">
      <RosterCompareButtonConsumer />
      <TotalStatsConsumer />
      <CompareVFXConsumer>
        <SuperstarCollapsibleGroup
          title="Upgrades and wins"
          name="upgrades_and_wins"
        >
          <UpgradesAndWinsConsumer />
        </SuperstarCollapsibleGroup>
      </CompareVFXConsumer>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SelectStarColorConsumer />
        <SelectStarConsumer />
      </div>
      <SuperstarLevelConsumer />
      <CompareVFXConsumer>
        <SuperstarCollapsibleGroup title="Moves" name="moves" defaultOpen>
          <AbilitiesConsumer />
        </SuperstarCollapsibleGroup>
      </CompareVFXConsumer>
    </div>
  );
};

const SuperstarCollapsibleGroup: FC<{
  defaultOpen?: boolean;
  title: string | React.ReactNode;
  name: string;
  tooltip?: string;
  children: React.ReactNode;
}> = ({ title, children, tooltip, name, defaultOpen = false }) => {
  const superstarId = useSuperstarDetailId();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger
        asChild
        onClick={() => {
          sendSuperstarDetailsSectionExpandEvent(superstarId, name);
        }}
      >
        <div className="drop-shadow-card relative flex h-12 items-center gap-2 rounded bg-[#272B4A] py-3 pl-4 [&[data-state=open]>svg]:rotate-180 [&[data-state=open]>svg]:text-[#06E775]">
          <div className="absolute inset-y-0 left-0 -z-10 w-1/2 overflow-hidden">
            <div
              data-state={isOpen ? 'open' : 'closed'}
              className="pointer-events-none absolute inset-0 transition-transform ease-in data-[state=closed]:-translate-x-full data-[state=open]:translate-x-0"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, #005840 0%, rgba(0, 95, 69, 0) 100%)',
              }}
            />
          </div>

          <div
            className={cn(
              'drop-shadow-text-2 text-sm font-bold uppercase italic',
              {
                'w-full': !tooltip,
              }
            )}
          >
            {title}
          </div>
          {tooltip && (
            <InfoTooltip
              titleClassName="h-4 w-4"
              content={tooltip}
              iconClassName="h-1.5"
            />
          )}

          <IcoMoonIcon
            className="text-text700 data-[expanded=true]:text-text500 absolute right-4 size-4 transition duration-500 ease-in-out"
            iconName="arrow"
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="ease-in-out-expo data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden bg-[#191D3C]">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};
